import type { Node as JsonNode, ParseError } from 'jsonc-parser';
import { msp360LogLevel } from '../formats/msp360-log';
import type { TextDocument } from '../formats/text-format';
import type { SizeUnits } from '../models/viewer-types';
import { setToolbarIcon } from './viewer-icons';
import { jsonTreeToYaml, xmlToYaml } from './yaml-preview';

const RICH_LIMIT = 2 * 1024 * 1024;
const HIGHLIGHT_LIMIT = 200000;
const MAX_LINES = 500000;
const MAX_LINE_LENGTH = 20000;
const MAX_MATCHES = 10000;
const ROW_HEIGHT = 22;
const TREE_PAGE = 200;
const MAX_XML_DEPTH = 100;

const PART_ATTRIBUTE = 'part';
const LABEL_ATTRIBUTE = 'aria-label';
const VIEW_LABEL = 'View';
const MSP360_LOG = 'msp360-log';

export interface TextViewerOptions {
  fileName?: string;
  wrapLines?: boolean;
  view?: string;
  sizeUnits?: SizeUnits;
  /** Where the toolbar goes, when it does not belong inside the preview itself. */
  controlsHost?: HTMLElement | null;
  isCurrent: () => boolean;
  getQuery: () => string;
  onMatches: (value: string) => void;
  onViewChange?: (view: string) => void;
  onWrapChange?: (value: boolean) => void;
}

export interface TextViewer {
  /** Settles once the first view is on screen, including a preview frame that has to load. */
  ready: Promise<void>;
  setWordWrap(value: boolean): void;
  setView(value: string): void;
  setSizeUnits(value: SizeUnits): void;
  search(query: string): void;
  jump(): void;
  destroy(): void;
}

/** The virtualized source rows, and the two things the rest of the viewer asks of them. */
interface SourcePane {
  element: HTMLDivElement;
  paint(): void;
  scrollToLine(line: number): void;
}

/** Where a match sits in the source: which line, and how far into it. */
interface SourceMatch {
  line: number;
  column: number;
}

/** One step of the iterative XML walk: an element to write, or the closing tag owed to one. */
interface XmlStep {
  node?: ChildNode;
  depth: number;
  close?: string;
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

/**
 * XML laid out by nesting depth. Elements that hold text of their own are written as they stand, so
 * that no whitespace is introduced where it would be part of the content.
 *
 * @param {Document} xml The parsed document.
 * @return {string} The document, indented.
 */
function formatXml(xml: Document): string {
  const serializer = new XMLSerializer();
  const output: Array<string> = [];
  const queue: Array<XmlStep> = [...xml.childNodes].reverse().map((node) => ({ node, depth: 0 }));

  while (queue.length) {
    const { node, depth, close } = queue.pop() as XmlStep;

    if (close) {
      output.push('  '.repeat(depth) + close);
      continue;
    }

    const element = node as ChildNode;
    const children = element.nodeType === 1 ? (element as Element).children : null;
    const mixed = [...element.childNodes].some((child) => (child.nodeType === 3 && child.textContent?.trim()) || child.nodeType === 4);

    if (depth > MAX_XML_DEPTH || element.nodeType !== 1 || !children?.length || mixed) {
      const value = serializer.serializeToString(element);

      if (value.trim()) output.push('  '.repeat(Math.min(depth, MAX_XML_DEPTH)) + value);

      continue;
    }

    const clone = element.cloneNode(false);
    const value = serializer.serializeToString(clone);

    output.push('  '.repeat(depth) + value.replace(/\s*\/>$/, '>'));
    queue.push({ close: `</${element.nodeName}>`, depth });

    for (const child of [...element.childNodes].reverse()) queue.push({ node: child, depth: depth + 1 });
  }

  return output.join('\n');
}

/**
 * A text preview: the document as it arrived, and whatever richer views its format allows — formatted
 * source, a JSON tree, YAML, or a rendered Markdown or HTML page in a frame of its own.
 *
 * @param {HTMLElement} root Where the preview is built.
 * @param {TextDocument} specification The text, its format, and the language to highlight it as.
 * @param {TextViewerOptions} options The toolbar's home, the search box, and where to report.
 * @return {Promise<TextViewer|null>} The preview, or null when the reader has moved on meanwhile.
 */
export async function createTextViewer(
  root: HTMLElement,
  specification: TextDocument,
  options: TextViewerOptions
): Promise<TextViewer | null> {
  const runtime = await import('./vendor/text-runtime');

  if (!options.isCurrent()) return null;

  const document = root.ownerDocument;
  const { text, type, language } = specification;

  let active = true;
  let frame: HTMLIFrameElement | null = null;
  let sourcePane: SourcePane | null = null;
  let sourceFrame = 0;
  let sourceObserver: ResizeObserver | null = null;
  let cancelFrame: (() => void) | null = null;
  let currentView = '';
  let lines: Array<string> = [];
  // Source views match by line and column; a rendered preview matches the marks put into its frame.
  let matches: Array<SourceMatch | HTMLElement> = [];
  let matchIndex = -1;
  let jsonTree: JsonNode | undefined;
  let formatted = text;
  let yaml: string | undefined;
  let yamlFactory: (() => string) | undefined;
  let wrapLines = Boolean(options.wrapLines);
  let initialReady = Promise.resolve();

  const controls = document.createElement('div');

  controls.className = 'text-controls';
  controls.setAttribute(PART_ATTRIBUTE, 'viewer-text-controls');

  const message = document.createElement('div');

  message.className = 'text-message';
  message.setAttribute('role', 'status');

  const body = document.createElement('div');

  body.className = 'text-body';
  root.replaceChildren(message, body);
  (options.controlsHost || root).prepend(controls);

  const views = new Map<string, () => void>();
  const viewMenu = document.createElement('select');

  viewMenu.className = 'view-menu';
  viewMenu.setAttribute(PART_ATTRIBUTE, 'viewer-view-select');
  viewMenu.setAttribute(LABEL_ATTRIBUTE, VIEW_LABEL);
  viewMenu.title = VIEW_LABEL;
  controls.append(viewMenu);

  function addView(label: string, view: string, action: () => void): void {
    const option = document.createElement('option');

    option.value = view;
    option.textContent = label;
    viewMenu.append(option);
    views.set(view, action);
  }

  viewMenu.onchange = (): void => {
    try {
      views.get(viewMenu.value)?.();
    } catch (error) {
      message.textContent = (error as Error).message;
      viewMenu.value = currentView;
    }
  };

  function select(view: string): void {
    const changed = currentView !== view;

    currentView = view;

    if (changed) options.onViewChange?.(view);

    viewMenu.value = view;
    frame?.remove();
    frame = null;
    cancelFrame?.();
    cancelFrame = null;
    cancelAnimationFrame(sourceFrame);
    sourceObserver?.disconnect();
    sourcePane = null;
    body.replaceChildren();
    matches = [];
    matchIndex = -1;
    options.onMatches('');
    wrapButton.hidden = view === 'preview' || view === 'tree';
  }

  function highlighted(value: string, name: string): string | null {
    return name && runtime.hljs.getLanguage(name) && value.length <= HIGHLIGHT_LIMIT
      ? runtime.hljs.highlight(value, { language: name, ignoreIllegals: true }).value
      : null;
  }

  // Highlighting returns one run of markup for the whole document, but the rows are drawn one at a
  // time: the markup is cut at the newlines, reopening on the next line whatever spans were still open.
  function tokenLines(value: string): Array<string> | null {
    if (type === MSP360_LOG) return null;

    const html = highlighted(value, currentView === 'yaml' ? 'yaml' : language);

    if (html == null) return null;

    const container = document.createElement('div');

    container.innerHTML = html;

    const output = [''];
    const tags: Array<string> = [];

    function visit(node: ChildNode): void {
      if (node.nodeType === 3) {
        const pieces = (node.textContent ?? '').split('\n');

        for (let index = 0; index < pieces.length; index++) {
          output[output.length - 1] += escapeHtml(pieces[index]);

          if (index < pieces.length - 1) {
            output[output.length - 1] += '</span>'.repeat(tags.length);
            output.push(tags.join(''));
          }
        }

        return;
      }

      const tag = `<span class="${((node as Element).className || '').replace(/[^a-z0-9 _-]/gi, '')}">`;

      tags.push(tag);
      output[output.length - 1] += tag;

      for (const child of node.childNodes) visit(child);

      output[output.length - 1] += '</span>';
      tags.pop();
    }

    for (const node of container.childNodes) visit(node);

    return output;
  }

  function source(value: string, view: string): void {
    select(view);

    const normalized = value.replace(/\r\n?/g, '\n');
    const tokens = tokenLines(normalized);

    lines = normalized.split('\n');

    if (lines.length > MAX_LINES) {
      lines.length = MAX_LINES;
      message.textContent = `Showing the first ${MAX_LINES.toLocaleString('en-US')} lines.`;
    }

    const scroll = document.createElement('div');

    scroll.className = 'source-scroll' + (wrapLines ? ' wrap-lines' : '');
    scroll.setAttribute(PART_ATTRIBUTE, 'viewer-source');
    scroll.tabIndex = 0;
    scroll.setAttribute(LABEL_ATTRIBUTE, 'Document source');

    const rows = document.createElement('div');

    rows.className = 'source-lines';
    scroll.append(rows);
    body.append(scroll);

    let offsets: Array<number> = [];
    let heights: Array<number> = [];
    let measuredWidth = -1;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;

    function rebuildOffsets(): void {
      offsets = [0];

      for (const height of heights) offsets.push((offsets.at(-1) as number) + height);

      rows.style.height = `${offsets.at(-1)}px`;
    }

    function lineAt(top: number): number {
      let low = 0;
      let high = lines.length;

      while (low < high) {
        const middle = (low + high) >>> 1;

        if (offsets[middle + 1] <= top) low = middle + 1;
        else high = middle;
      }

      return Math.min(low, Math.max(0, lines.length - 1));
    }

    function measure(width: number): void {
      const anchor = offsets.length ? lineAt(scroll.scrollTop) : 0;
      const inset = offsets.length ? scroll.scrollTop - offsets[anchor] : 0;

      measuredWidth = width;

      const styles = getComputedStyle(scroll);

      context.font = styles.font;

      const columns = Math.max(1, Math.floor((width - 88) / (context.measureText('M').width || 8)));

      heights = lines.map((line) =>
        wrapLines
          ? Math.max(1, Math.ceil(Math.min(MAX_LINE_LENGTH, line.replace(/\t/g, '    ').length) / columns)) * ROW_HEIGHT
          : ROW_HEIGHT
      );
      rebuildOffsets();
      scroll.scrollTop = offsets[anchor] + inset;
    }

    function drawRow(index: number): HTMLDivElement {
      const row = document.createElement('div');

      row.className = 'source-line';
      row.style.top = `${offsets[index]}px`;

      if (type === MSP360_LOG) {
        const level = msp360LogLevel(lines[index]);

        if (level) row.classList.add(`log-${level}`);
      }

      row.dataset.line = String(index + 1);

      const number = document.createElement('span');

      number.className = 'line-number';
      number.setAttribute(PART_ATTRIBUTE, 'viewer-line-number');
      number.textContent = String(index + 1);
      number.setAttribute('aria-hidden', 'true');

      const code = document.createElement('code');

      code.className = 'line-code';

      const hit = matches[matchIndex] as SourceMatch | undefined;
      // A match far along a very long line would be cut away; the window starts just before it instead.
      const start = hit?.line === index ? Math.max(0, hit.column - 100) : 0;
      const value = lines[index].slice(start, start + MAX_LINE_LENGTH);
      const html =
        !start && lines[index].length <= MAX_LINE_LENGTH && tokens
          ? tokens[index]
          : type === MSP360_LOG
            ? null
            : highlighted(value, currentView === 'yaml' ? 'yaml' : language);

      if (html != null) code.innerHTML = html;
      else code.textContent = value || ' ';

      if (start) code.prepend(document.createTextNode('…'));

      if (lines[index].length > start + MAX_LINE_LENGTH) {
        code.append(document.createTextNode('… [long line clipped]'));
      }

      if (hit?.line === index) row.classList.add('source-match');

      row.append(number, code);

      return row;
    }

    function paint(): void {
      if (!active || sourcePane?.element !== scroll) return;

      const width = scroll.clientWidth;

      if (width !== measuredWidth) measure(width);

      const first = Math.max(0, lineAt(scroll.scrollTop) - 8);
      const end = Math.min(lines.length, lineAt(scroll.scrollTop + scroll.clientHeight) + 21);
      const fragment = document.createDocumentFragment();

      for (let index = first; index < end; index++) fragment.append(drawRow(index));

      rows.replaceChildren(fragment);

      if (!wrapLines) return;

      // Wrapped rows are only as tall as the browser made them, which is known once they are placed.
      let changed = false;

      for (const row of [...rows.children] as Array<HTMLElement>) {
        const index = Number(row.dataset.line) - 1;
        const height = Math.max(ROW_HEIGHT, row.getBoundingClientRect().height);

        if (heights[index] !== height) {
          heights[index] = height;
          changed = true;
        }
      }

      if (changed) {
        rebuildOffsets();

        for (const row of [...rows.children] as Array<HTMLElement>) {
          row.style.top = `${offsets[Number(row.dataset.line) - 1]}px`;
        }
      }
    }

    scroll.onscroll = (): void => {
      cancelAnimationFrame(sourceFrame);
      sourceFrame = requestAnimationFrame(paint);
    };

    sourcePane = {
      element: scroll,
      paint,
      scrollToLine: (line: number): void => {
        scroll.scrollTop = offsets[line] - 44;
      }
    };
    sourceObserver = new ResizeObserver(paint);
    sourceObserver.observe(scroll);
    paint();
    search(options.getQuery());
  }

  function treeNode(node: JsonNode, name: string | number | null): HTMLDivElement {
    const container = document.createElement('div');

    container.className = 'json-node';

    const composite = node.type === 'object' || node.type === 'array';
    const caption = document.createElement(composite ? 'summary' : 'div');

    caption.className = 'json-caption';

    const key = document.createElement('span');

    key.className = 'json-key';
    key.textContent = name == null ? '' : `${name}: `;

    const value = document.createElement('span');

    value.className = `json-${node.type}`;
    value.textContent = composite
      ? `${node.type === 'object' ? '{…}' : '[…]'} · ${node.children?.length || 0} items`
      : text.slice(node.offset, node.offset + Math.min(node.length, MAX_LINE_LENGTH));
    caption.append(key, value);

    if (!composite) {
      container.append(caption);

      return container;
    }

    const details = document.createElement('details');

    details.append(caption);
    container.append(details);

    const children = document.createElement('div');

    children.className = 'json-children';

    const more = document.createElement('button');

    more.type = 'button';
    more.textContent = 'Show next 200 items';

    let shown = 0;

    function add(): void {
      const entries = node.children || [];
      const end = Math.min(entries.length, shown + TREE_PAGE);

      for (; shown < end; shown++) {
        const entry = entries[shown];
        const pair = entry.children as Array<JsonNode>;

        children.append(node.type === 'object' ? treeNode(pair[1], pair[0].value as string) : treeNode(entry, shown));
      }

      more.remove();

      if (shown < entries.length) children.append(more);
    }

    more.onclick = add;
    details.ontoggle = (): void => {
      if (details.open && !shown) {
        details.append(children);
        add();
      }
    };

    return container;
  }

  function tree(): void {
    select('tree');

    const rendered = treeNode(jsonTree as JsonNode, null);

    rendered.classList.add('json-tree');
    body.append(rendered);
    rendered.querySelector('details')?.setAttribute('open', '');
    message.textContent = 'Find searches the formatted JSON text.';

    if (options.getQuery()) source(formatted, 'formatted');
  }

  // Everything that could reach back out of the frame is taken away before it is written: scripts and
  // plugins, remote images, link targets, and anything that could navigate or submit on its own.
  function inertDocument(value: string): Document {
    const sanitized = runtime.DOMPurify.sanitize(value, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ['style'],
      FORBID_TAGS: [
        'script',
        'iframe',
        'frame',
        'frameset',
        'object',
        'embed',
        'base',
        'meta',
        'link',
        'form',
        'input',
        'textarea',
        'select',
        'button',
        'video',
        'audio',
        'source',
        'track',
        'svg',
        'math'
      ],
      FORBID_ATTR: ['srcset', 'ping', 'action', 'formaction', 'poster', 'background', 'contenteditable', 'autofocus']
    });
    const inert = new DOMParser().parseFromString(sanitized, 'text/html');

    for (const link of inert.querySelectorAll('a')) {
      if (!link.getAttribute('href')?.startsWith('#')) link.removeAttribute('href');

      link.removeAttribute('target');
    }

    for (const element of inert.querySelectorAll('[src]')) {
      if (!/^data:image\/(?:png|jpeg|gif|webp|avif);base64,/i.test(element.getAttribute('src') ?? '')) {
        element.removeAttribute('src');
      }
    }

    return inert;
  }

  function markup(value: string, view: string): void {
    select(view);

    const inert = inertDocument(value);
    const current = document.createElement('iframe');

    frame = current;
    current.className = 'markup-frame';
    current.title = type === 'html' ? 'HTML preview' : 'Markdown preview';
    current.setAttribute('sandbox', 'allow-same-origin');
    current.referrerPolicy = 'no-referrer';

    initialReady = new Promise<void>((resolve) => {
      current.onload = (): void => {
        if (active && frame === current) {
          cancelFrame = null;
          search(options.getQuery());
        }

        resolve();
      };
      cancelFrame = (): void => {
        current.onload = null;
        current.removeAttribute('srcdoc');
        current.remove();
        resolve();
      };
    });

    const css =
      'html{color:#20314a;background:white;font:15px/1.6 system-ui,sans-serif}body{margin:24px;overflow-wrap:anywhere}pre{overflow:auto;padding:14px;background:#f4f6f8}code{font-family:ui-monospace,monospace}table{border-collapse:collapse}td,th{padding:8px;border:1px solid #dce4ed}img{max-width:100%}mark.viewer-match{background:#ffdf83;color:inherit}mark.viewer-match.current{background:#ffa94d;outline:2px solid #b56b00}';
    const styles = [...inert.querySelectorAll('head style')].map((style) => style.outerHTML).join('');

    current.srcdoc =
      '<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" ' +
      `content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; ` +
      `base-uri 'none'; form-action 'none';"><style>${css}</style>${styles}</head>` +
      `<body>${inert.body.innerHTML}</body></html>`;
    body.append(current);
  }

  function searchSource(query: string): void {
    if (!query) return;

    const lower = query.toLocaleLowerCase();

    for (let line = 0; line < lines.length && matches.length < MAX_MATCHES; line++) {
      const value = lines[line].toLocaleLowerCase();
      let from = 0;
      let column = value.indexOf(lower, from);

      while (column >= 0 && matches.length < MAX_MATCHES) {
        matches.push({ line, column });
        from = column + Math.max(1, lower.length);
        column = value.indexOf(lower, from);
      }
    }
  }

  function searchFrame(query: string): void {
    const doc = (frame as HTMLIFrameElement).contentDocument as Document;

    for (const mark of doc.querySelectorAll('mark.viewer-match')) {
      mark.replaceWith(doc.createTextNode(mark.textContent ?? ''));
    }

    doc.body.normalize();

    if (!query) return;

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node: Node): number =>
        node.parentElement?.closest('style,script') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    const nodes: Array<ChildNode> = [];

    while (walker.nextNode()) nodes.push(walker.currentNode as ChildNode);

    const lower = query.toLocaleLowerCase();

    for (const node of nodes) {
      const content = node.textContent ?? '';
      const value = content.toLocaleLowerCase();
      const fragment = doc.createDocumentFragment();
      let from = 0;
      let at = value.indexOf(lower, from);

      while (at >= 0 && matches.length < MAX_MATCHES) {
        fragment.append(doc.createTextNode(content.slice(from, at)));

        const mark = doc.createElement('mark');

        mark.className = 'viewer-match';
        mark.textContent = content.slice(at, at + query.length);
        fragment.append(mark);
        matches.push(mark);
        from = at + query.length;
        at = value.indexOf(lower, from);
      }

      if (from) {
        fragment.append(doc.createTextNode(content.slice(from)));
        node.replaceWith(fragment);
      }
    }
  }

  function search(query: string): void {
    if (!active) return;

    // The tree has nothing to search through; the formatted text it was built from does.
    if (currentView === 'tree' && query) {
      source(formatted, 'formatted');

      return;
    }

    matches = [];
    matchIndex = -1;

    if (sourcePane) {
      searchSource(query);
      sourcePane.paint();
    } else if (frame?.contentDocument?.body) {
      searchFrame(query);
    }

    options.onMatches(query ? (matches.length ? '' : 'No matches') : '');

    if (matches.length) jump();
  }

  function jump(): void {
    if (!matches.length) return;

    if (!sourcePane) (matches[matchIndex] as HTMLElement | undefined)?.classList.remove('current');

    matchIndex = (matchIndex + 1) % matches.length;

    if (sourcePane) {
      const match = matches[matchIndex] as SourceMatch;

      sourcePane.scrollToLine(match.line);
      sourcePane.paint();
    } else {
      const match = matches[matchIndex] as HTMLElement;

      match.classList.add('current');
      match.scrollIntoView({ block: 'center' });
    }

    options.onMatches(`${matchIndex + 1} / ${matches.length}${matches.length === MAX_MATCHES ? '+' : ''}`);
  }

  const wrapButton = document.createElement('button');

  wrapButton.type = 'button';
  wrapButton.className = 'wrap-toggle';
  wrapButton.setAttribute(PART_ATTRIBUTE, 'viewer-wrap');
  wrapButton.setAttribute(LABEL_ATTRIBUTE, 'Wrap lines');
  wrapButton.title = 'Wrap lines';
  setToolbarIcon(wrapButton, 'wrap');
  wrapButton.setAttribute('aria-pressed', String(wrapLines));

  function setWordWrap(value: boolean): void {
    const wrap = Boolean(value);

    if (wrapLines === wrap) return;

    const top = sourcePane?.element.scrollTop || 0;

    wrapLines = wrap;
    wrapButton.setAttribute('aria-pressed', String(wrapLines));

    if (wrapButton.hidden) return;

    source(currentView === 'yaml' ? (yaml as string) : currentView === 'formatted' ? formatted : text, currentView);

    if (!options.getQuery() && sourcePane) {
      sourcePane.element.scrollTop = top;
      sourcePane.paint();
    }
  }

  wrapButton.onclick = (): void => {
    setWordWrap(!wrapLines);
    options.onWrapChange?.(wrapLines);
  };
  controls.append(wrapButton);

  const basename = (options.fileName || '').split(/[\\/]/).pop() as string;
  const extension = /\.[^.]+$/.exec(basename)?.[0];

  const suffix = extension ? ` (${extension})` : '';

  addView(`Original${suffix}`, 'source', () => source(text, 'source'));

  let initial: (() => void) | undefined;

  if (type === 'json' && text.length <= RICH_LIMIT) {
    const errors: Array<ParseError> = [];
    let failedAt: number | null = null;

    try {
      jsonTree = runtime.parseTree(text, errors, {
        disallowComments: true,
        allowTrailingComma: false,
        allowEmptyContent: false
      });

      if (errors.length) failedAt = errors[0].offset;
    } catch {
      // Nesting too deep to walk is thrown rather than reported, and counts as a parse failure.
      failedAt = 0;
    }

    if (failedAt === null && jsonTree) {
      formatted = runtime.applyEdits(text, runtime.format(text, undefined, { insertSpaces: true, tabSize: 2, eol: '\n' }));
      addView('Tree (JSON)', 'tree', tree);
      yamlFactory = (): string => jsonTreeToYaml(jsonTree as JsonNode, text, options.sizeUnits);
      addView(`Formatted (${type.toUpperCase()})`, 'formatted', () => source(formatted, 'formatted'));
      initial = tree;
    } else {
      const where = failedAt === null ? '' : ` (offset ${failedAt})`;

      message.textContent = `Invalid JSON. Showing the original source${where}.`;
    }
  }

  if (type === 'xml' && text.length <= RICH_LIMIT) {
    if (/<!DOCTYPE|<!ENTITY/i.test(text)) {
      message.textContent = 'DTD declarations are not processed. Showing the original source.';
    } else {
      const xml = new DOMParser().parseFromString(text, 'application/xml');

      if (xml.querySelector('parsererror')) message.textContent = 'Invalid XML. Showing the original source.';
      else {
        formatted = formatXml(xml);
        yamlFactory = (): string => xmlToYaml(xml, options.sizeUnits);
        addView(`Formatted (${type.toUpperCase()})`, 'formatted', () => source(formatted, 'formatted'));
        initial = (): void => source(formatted, 'formatted');
      }
    }
  }

  if (['markdown', 'html'].includes(type) && text.length <= RICH_LIMIT) {
    const preview = (): void =>
      markup(type === 'markdown' ? (runtime.marked.parse(text, { async: false, gfm: true }) as string) : text, 'preview');

    addView(`Preview (${type === 'html' ? 'HTML' : 'Markdown'})`, 'preview', preview);

    if (type === 'markdown') initial = preview;
  }

  if (text.length > RICH_LIMIT && ['markdown', 'html', 'json', 'xml'].includes(type)) {
    message.textContent = 'Rich preview is limited to 2,097,152 characters. Showing the original source.';
  }

  if (yamlFactory) {
    addView('YAML', 'yaml', () => {
      if (yaml == null) yaml = (yamlFactory as () => string)();

      source(yaml, 'yaml');
    });
  }

  const viewOrder = ['source', 'formatted', 'yaml', 'preview', 'tree'];

  viewMenu.append(...[...viewMenu.options].sort((a, b) => viewOrder.indexOf(a.value) - viewOrder.indexOf(b.value)));
  viewMenu.hidden = views.size < 2;

  const defaultView =
    type === 'json' && views.has('tree')
      ? 'tree'
      : type === 'xml' && views.has('formatted')
        ? 'formatted'
        : type === 'markdown' && views.has('preview')
          ? 'preview'
          : 'source';

  (views.get(options.view ?? '') || initial || ((): void => source(text, 'source')))();

  return {
    ready: initialReady,
    setWordWrap,
    setView(value: string): void {
      const view = value === 'auto' ? defaultView : value;
      const action = views.get(view);

      if (action && view !== currentView) {
        try {
          action();
        } catch (error) {
          message.textContent = (error as Error).message;
        }
      }
    },
    setSizeUnits(value: SizeUnits): void {
      options.sizeUnits = value;
    },
    search,
    jump,
    destroy(): void {
      active = false;
      sourceObserver?.disconnect();
      cancelAnimationFrame(sourceFrame);
      cancelFrame?.();
      frame?.remove();
      controls.remove();
      root.replaceChildren();
      matches = [];
      lines = [];
    }
  };
}
